import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import {
  documentPageCanvasSx
} from '../constants/documentTheme';
import {
  PARTNER_PLANS,
  PARTNER_TRIAL_DAYS,
  PARTNER_PROFESSIONAL_ONBOARDING_FEE,
  PARTNER_DEVELOPER_API_MONTHLY,
  buildPartnerLaunchComparison,
  getPartnerPlan,
  partnerSalesMailto,
  introductionFeeBounds,
  introductionFeeCopyVars,
  introductionFeeLadder,
} from '../config/plans';
import { planNameKey } from '../utils/partnerPlan';
import { developerPublicBase } from '../utils/developerHost';
import {
  ComparisonTable,
  CtaPair,
  EnterpriseCallout,
  Eyebrow,
  FaqList,
  OnboardingCallout,
  BillingIntervalToggle,
  pricingFootSx as FOOT_SX,
  pricingHeadingSx as HEADING_SX,
  pricingPaperSx as PAPER_SX,
  pricingWrapSx as WRAP_SX
} from '../components/PricingChrome';

// Keeps a linked section clear of the fixed navbar when it is scrolled to.
const ANCHOR_SX = { scrollMarginTop: { xs: 72, md: 96 } };

const PARTNER_FAQ_IDS = [
  'howMuch',
  'feeBasis',
  'ai',
  'whoHoldsFunds',
  'whoseBrand',
  'traveler',
  'seatLimit',
  'cancel',
  'export'
];

const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [interval, setInterval] = useState('monthly');
  const partnerName = useCallback((id) => t(planNameKey(id)), [t]);

  useEffect(() => {
    if (!hash) return undefined;
    const timer = window.setTimeout(() => {
      const target = document.getElementById(hash.slice(1));
      if (typeof target?.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [hash]);

  const partnerFaq = useMemo(
    () => PARTNER_FAQ_IDS.map((id) => ({
      id,
      question: t(`pricingPage.faq.partner.${id}.q`),
      answer: t(`pricingPage.faq.partner.${id}.a`, {
        trialDays: PARTNER_TRIAL_DAYS,
        proSeats: getPartnerPlan('pro').seats,
        proMonthly: getPartnerPlan('pro').monthly,
        ...introductionFeeCopyVars(),
        feeMin: introductionFeeBounds().min,
        feeMax: introductionFeeBounds().max,
        feeLadder: introductionFeeLadder(partnerName)
      })
    })),
    [t, partnerName]
  );

  const partnerComparison = useMemo(
    () => buildPartnerLaunchComparison({ t, interval, partnerName }),
    [t, interval, partnerName]
  );

  const structuredData = useMemo(() => {
    const offers = PARTNER_PLANS.filter((plan) => plan.selfServe).map((plan) => ({
      '@type': 'Offer',
      name: partnerName(plan.id),
      price: String(plan.monthly),
      priceCurrency: 'USD',
      url: 'https://dejavunomad.com/pricing',
      availability: 'https://schema.org/InStock'
    }));

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Déjà Vu Nomad Partner Plans',
        description: t('pricingPage.seoDescription'),
        brand: { '@type': 'Brand', name: 'Déjà Vu Nomad' },
        offers
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: partnerFaq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer }
        }))
      }
    ];
  }, [t, partnerName, partnerFaq]);

  const feeCopy = introductionFeeCopyVars();
  const salesMailto = partnerSalesMailto();

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title={t('pricingPage.seoTitle')}
        description={t('pricingPage.seoDescription')}
        keywords={t('pricingPage.seoKeywords')}
        canonical="https://dejavunomad.com/pricing"
        structuredData={structuredData}
      />
      <DocumentMarketingHero
        eyebrow={t('pricingPage.eyebrow')}
        title={t('pricingPage.title')}
        disclaimer={t('pricingPage.processingDisclaimer')}
        subtitle={t('pricingPage.lede')}
        subtitleDetail={t('pricingPage.ledeDetail', feeCopy)}
      />

      <Box sx={{ py: { xs: 4, md: 7 } }}>
        <Box sx={WRAP_SX}>
          <Box id="compare" sx={{ ...PAPER_SX, ...ANCHOR_SX }}>
            <Eyebrow>{t('pricingPage.partner.eyebrow')}</Eyebrow>
            <Typography component="h2" sx={HEADING_SX}>
              {t('pricingPage.compare.partnerTitle')}
            </Typography>

            <BillingIntervalToggle
              value={interval}
              onChange={setInterval}
              ariaLabel={t('pricingPage.partner.billingLabel')}
              monthlyLabel={t('pricingPage.partner.monthly')}
              yearlyLabel={t('pricingPage.partner.yearly')}
              yearlySavingLabel={t('pricingPage.partner.yearlySaving')}
              sx={{ mb: 3.25 }}
            />

            <ComparisonTable
              caption={t('pricingPage.compare.partnerCaption')}
              columns={partnerComparison.columns}
              rows={partnerComparison.rows}
              yesLabel={t('pricingPage.compare.included')}
              noLabel={t('pricingPage.compare.notIncluded')}
            />

            <EnterpriseCallout
              title={t('pricingPage.enterprise.title')}
              body={t('pricingPage.enterprise.body')}
              ctaHref={salesMailto}
              ctaLabel={t('pricingPage.enterprise.cta')}
            />

            <OnboardingCallout
              title={t('pricingPage.onboarding.title', { fee: PARTNER_PROFESSIONAL_ONBOARDING_FEE })}
              body={t('pricingPage.onboarding.body')}
              ctaHref={salesMailto}
              ctaLabel={t('pricingPage.onboarding.cta')}
            />

            <OnboardingCallout
              eyebrow={t('pricingPage.developerApi.eyebrow')}
              title={t('pricingPage.developerApi.title', { fee: PARTNER_DEVELOPER_API_MONTHLY })}
              body={t('pricingPage.developerApi.body')}
              enterpriseNote={t('pricingPage.developerApi.note')}
              ctaHref={`${developerPublicBase()}/docs`}
              ctaLabel={t('pricingPage.developerApi.ctaDocs')}
              secondaryCtaHref="/partner/billing"
              secondaryCtaLabel={t('pricingPage.developerApi.ctaAdd')}
            />

            <CtaPair
              primary={t('pricingPage.partner.cta')}
              onPrimary={() => navigate('/partner/signup')}
              secondary={t('pricingPage.partner.secondary')}
              onSecondary={() => navigate('/partner#partner-demo')}
            />
          </Box>

          <Typography sx={FOOT_SX}>
            {t('pricingPage.partnerFoot', { trialDays: PARTNER_TRIAL_DAYS, ...feeCopy })}
          </Typography>

          <Box id="faq" sx={{ ...PAPER_SX, ...ANCHOR_SX, mt: { xs: 4, md: 6 } }}>
            <Eyebrow>{t('pricingPage.faq.eyebrow')}</Eyebrow>
            <Typography component="h2" sx={HEADING_SX}>
              {t('pricingPage.faq.partnerTitle')}
            </Typography>
            <FaqList items={partnerFaq} />
          </Box>
        </Box>
      </Box>

    </Box>
  );
};

export default Pricing;
